/**
 * Self-Learning Memory System
 * 
 * Four-layer memory architecture:
 * 1. Episodic Memory - What happened (vector database)
 * 2. Semantic Memory - General knowledge (user preferences, patterns)
 * 3. Procedural Memory - How to do things (learned workflows)
 * 4. Working Memory - Current context (Redis-like cache)
 * 
 * Enables continuous learning and pattern recognition for autonomous operation
 */

import { blink } from '@/lib/blink';
import { safeJsonParse } from '@/lib/utils';



/**
 * Episodic Memory - Stores every interaction with full context
 */
interface EpisodicMemory {
  id: string;
  userId: string;
  timestamp: Date;
  type: 'email' | 'meeting' | 'interaction' | 'decision';
  context: {
    what: string; // What happened
    who: string[]; // Who was involved
    where?: string; // Location or channel
    why?: string; // Purpose or goal
    outcome: string; // Result of interaction
    sentiment: 'positive' | 'neutral' | 'negative';
  };
  embedding?: number[]; // Vector embedding for similarity search
  relatedMemories: string[]; // IDs of related episodic memories
  tags: string[];
}

/**
 * Semantic Memory - User preferences and general knowledge
 */
interface SemanticMemory {
  userId: string;
  meetingPreferences: {
    preferredTimes: Record<string, { preferred: string; avoid: string }>;
    maxMeetingsPerDay: number;
    bufferTimeBetweenMeetings: number; // minutes
    preferredDuration: number; // minutes
    focusTimeBlocks: Array<{ day: string; start: string; end: string }>;
  };
  communicationStyle: {
    byContact: Record<string, {
      tone: 'formal' | 'casual' | 'technical';
      responseTimeExpectation: number; // hours
      preferredChannel: 'email' | 'chat' | 'call';
    }>;
    byContext: Record<string, {
      executives: string;
      team: string;
      clients: string;
      vendors: string;
    }>;
  };
  priorityRules: {
    contacts: string[]; // VIP contacts
    topics: string[]; // Important keywords
    autoDecline: string[]; // Patterns to auto-decline
    autoAccept: string[]; // Patterns to auto-accept
  };
  workingPatterns: {
    typicalWorkHours: { start: number; end: number };
    daysOff: string[];
    peakProductivityHours: number[];
    lowEnergyHours: number[];
  };
  learnedPreferences: Record<string, any>; // Dynamic preferences learned over time
  confidenceScores: Record<string, number>; // How confident we are about each preference
}

/**
 * Procedural Memory - Learned workflows and task patterns
 */
interface ProceduralMemory {
  id: string;
  userId: string;
  pattern: {
    trigger: string; // What triggers this workflow
    conditions: Array<{
      field: string;
      operator: 'equals' | 'contains' | 'matches';
      value: string;
    }>;
    actions: Array<{
      type: string;
      parameters: Record<string, any>;
      order: number;
    }>;
  };
  confidence: number; // 0-1, how confident we are about this pattern
  timesObserved: number;
  timesExecuted: number;
  successRate: number;
  lastUsed: Date;
  metadata: {
    description: string;
    examples: string[];
    learnedFrom: string[]; // IDs of episodic memories
  };
}

/**
 * Working Memory - Active context for current tasks
 */
interface WorkingMemory {
  userId: string;
  activeProjects: Array<{
    id: string;
    name: string;
    status: 'active' | 'blocked' | 'completed';
    priority: 'critical' | 'high' | 'medium' | 'low';
    deadline?: Date;
    dependencies: string[];
    recentActivity: Date;
  }>;
  currentContext: {
    lastEmails: Array<{ id: string; timestamp: Date; from: string; subject: string }>;
    lastMeetings: Array<{ id: string; timestamp: Date; with: string[]; topic: string }>;
    pendingDecisions: Array<{ id: string; description: string; dueBy: Date }>;
    recentInteractions: Array<{ contact: string; timestamp: Date; type: string }>;
  };
  shortTermGoals: string[];
  ttl: number; // Time to live in seconds
}

export class SelfLearningMemorySystem {
  private userId: string | null = null;

  async initialize(userId: string): Promise<void> {
    this.userId = userId;
    await this.ensureUserMemoryStructure();
  }

  /**
   * Ensure user has memory structure in database
   */
  private async ensureUserMemoryStructure(): Promise<void> {
    if (!this.userId) return;

    // Check if semantic memory exists
    const existing = await blink.db.userModels.list({
      where: { userId: this.userId },
      limit: 1
    });

    if (existing.length === 0) {
      // Create initial semantic memory
      await blink.db.userModels.create({
        id: `model_${Date.now()}`,
        userId: this.userId,
        modelStatus: 'learning',
        confidenceScore: 0,
        totalEmailsAnalyzed: 0,
        personalityTraits: JSON.stringify({}),
        workStyle: JSON.stringify({}),
        communicationPreferences: JSON.stringify(this.getDefaultSemanticMemory()),
        priorities: JSON.stringify([]),
        decisionMakingStyle: JSON.stringify({}),
        behavioralSummary: '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
    }
  }

  /**
   * Store episodic memory
   */
  async storeEpisode(episode: Omit<EpisodicMemory, 'id' | 'embedding'>): Promise<void> {
    if (!this.userId) throw new Error('Memory system not initialized');

    // Generate embedding for similarity search
    const contextText = `${episode.context.what} ${episode.context.who.join(' ')} ${episode.context.outcome}`;
    
    // Store in database (using search_index table for vector-like capabilities)
    await blink.db.searchIndex.create({
      id: `episode_${Date.now()}_${Math.random().toString(36).substring(7)}`,
      userId: this.userId,
      itemType: episode.type,
      itemId: episode.timestamp.toISOString(),
      title: episode.context.what,
      content: JSON.stringify(episode.context),
      metadata: JSON.stringify({
        type: episode.type,
        who: episode.context.who,
        sentiment: episode.context.sentiment,
        tags: episode.tags,
        relatedMemories: episode.relatedMemories
      }),
      searchVector: contextText, // Simplified - would use actual vector in production
      updatedAt: episode.timestamp.toISOString()
    });
  }

  /**
   * Retrieve similar episodes
   */
  async recallSimilarEpisodes(query: string, limit: number = 5): Promise<EpisodicMemory[]> {
    if (!this.userId) throw new Error('Memory system not initialized');

    // Search using search_index
    const results = await blink.db.searchIndex.list({
      where: { userId: this.userId },
      limit: 20
    });

    // Score and sort by relevance (simplified - would use vector similarity in production)
    const scored = results
      .map(r => {
        const metadata = typeof r.metadata === 'string' ? safeJsonParse(r.metadata) : r.metadata;
        const context = typeof r.content === 'string' ? safeJsonParse(r.content) : r.content;
        
        const relevance = this.calculateRelevance(query, r.searchVector || '', r.title || '');
        
        return {
          score: relevance,
          episode: {
            id: r.id,
            userId: r.userId,
            timestamp: new Date(r.itemId),
            type: r.itemType as any,
            context,
            tags: metadata.tags || [],
            relatedMemories: metadata.relatedMemories || []
          }
        };
      })
      .sort((a, b) => b.score - a.score)
      .slice(0, limit)
      .map(item => item.episode);

    return scored;
  }

  /**
   * Update semantic memory from observations
   */
  async updateSemanticMemory(updates: Partial<SemanticMemory>): Promise<void> {
    if (!this.userId) throw new Error('Memory system not initialized');

    const existing = await blink.db.userModels.list({
      where: { userId: this.userId },
      limit: 1
    });

    if (existing.length === 0) {
      await this.ensureUserMemoryStructure();
      return this.updateSemanticMemory(updates);
    }

    const model = existing[0];
    const currentPrefs = typeof model.communicationPreferences === 'string'
      ? safeJsonParse(model.communicationPreferences)
      : model.communicationPreferences || {};

    const merged = this.deepMerge(currentPrefs, updates);

    await blink.db.userModels.update(model.id, {
      communicationPreferences: JSON.stringify(merged),
      updatedAt: new Date().toISOString()
    });
  }

  /**
   * Get semantic memory
   */
  async getSemanticMemory(): Promise<SemanticMemory> {
    if (!this.userId) throw new Error('Memory system not initialized');

    const models = await blink.db.userModels.list({
      where: { userId: this.userId },
      limit: 1
    });

    if (models.length === 0) {
      return this.getDefaultSemanticMemory();
    }

    const model = models[0];
    const prefs = typeof model.communicationPreferences === 'string'
      ? safeJsonParse(model.communicationPreferences)
      : model.communicationPreferences;

    return prefs || this.getDefaultSemanticMemory();
  }

  /**
   * Learn procedural pattern from observations
   */
  async learnProcedure(observation: {
    trigger: string;
    userActions: Array<{ type: string; parameters: any }>;
    context: any;
  }): Promise<void> {
    if (!this.userId) throw new Error('Memory system not initialized');

    // Check if similar pattern exists
    const existing = await this.findSimilarProcedure(observation.trigger);

    if (existing) {
      // Increment observation count and update confidence
      existing.timesObserved++;
      existing.confidence = Math.min(existing.confidence + 0.1, 1.0);
      existing.lastUsed = new Date();

      // Store updated procedure
      await blink.db.searchIndex.update(existing.id, {
        content: JSON.stringify(existing),
        updatedAt: new Date().toISOString()
      });
    } else {
      // Create new procedural memory
      const procedure: Omit<ProceduralMemory, 'id'> = {
        userId: this.userId,
        pattern: {
          trigger: observation.trigger,
          conditions: this.extractConditions(observation.context),
          actions: observation.userActions.map((action, idx) => ({
            type: action.type,
            parameters: action.parameters,
            order: idx + 1
          }))
        },
        confidence: 0.3, // Start with low confidence
        timesObserved: 1,
        timesExecuted: 0,
        successRate: 0,
        lastUsed: new Date(),
        metadata: {
          description: `Learned workflow for: ${observation.trigger}`,
          examples: [JSON.stringify(observation)],
          learnedFrom: []
        }
      };

      await blink.db.searchIndex.create({
        id: `procedure_${Date.now()}`,
        userId: this.userId,
        itemType: 'procedural_memory',
        itemId: observation.trigger,
        title: observation.trigger,
        content: JSON.stringify(procedure),
        metadata: JSON.stringify({ type: 'procedure' }),
        searchVector: observation.trigger,
        updatedAt: new Date().toISOString()
      });
    }
  }

  /**
   * Get matching procedures for a trigger
   */
  async getMatchingProcedures(trigger: string, minConfidence: number = 0.7): Promise<ProceduralMemory[]> {
    if (!this.userId) throw new Error('Memory system not initialized');

    const results = await blink.db.searchIndex.list({
      where: {
        userId: this.userId,
        itemType: 'procedural_memory'
      },
      limit: 10
    });

    return results
      .map(r => {
        const procedure = typeof r.content === 'string' ? safeJsonParse(r.content) : r.content;
        return { ...procedure, id: r.id } as ProceduralMemory;
      })
      .filter(p => {
        const matches = this.calculateRelevance(trigger, p.pattern.trigger, p.pattern.trigger) > 0.5;
        return matches && p.confidence >= minConfidence;
      })
      .sort((a, b) => b.confidence - a.confidence);
  }

  /**
   * Update working memory
   */
  async updateWorkingMemory(updates: Partial<WorkingMemory>): Promise<void> {
    if (!this.userId) throw new Error('Memory system not initialized');

    // Store in user preferences with TTL-like behavior
    const prefs = await blink.db.userPreferences.list({
      where: { userId: this.userId },
      limit: 1
    });

    const currentWorking = prefs.length > 0 && prefs[0].preferences
      ? (typeof prefs[0].preferences === 'string' 
          ? safeJsonParse(prefs[0].preferences) 
          : prefs[0].preferences)
      : {};

    const workingMemory = currentWorking.workingMemory || {};
    const merged = { ...workingMemory, ...updates, lastUpdated: new Date().toISOString() };

    if (prefs.length > 0) {
      await blink.db.userPreferences.update(prefs[0].id, {
        preferences: JSON.stringify({
          ...currentWorking,
          workingMemory: merged
        }),
        updatedAt: new Date().toISOString()
      });
    } else {
      await blink.db.userPreferences.create({
        id: `pref_${Date.now()}`,
        userId: this.userId,
        preferences: JSON.stringify({ workingMemory: merged }),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
    }
  }

  /**
   * Get working memory
   */
  async getWorkingMemory(): Promise<WorkingMemory> {
    if (!this.userId) throw new Error('Memory system not initialized');

    const prefs = await blink.db.userPreferences.list({
      where: { userId: this.userId },
      limit: 1
    });

    if (prefs.length === 0 || !prefs[0].preferences) {
      return this.getDefaultWorkingMemory();
    }

    const prefsObj = typeof prefs[0].preferences === 'string'
      ? safeJsonParse(prefs[0].preferences)
      : prefs[0].preferences;

    return prefsObj.workingMemory || this.getDefaultWorkingMemory();
  }

  /**
   * Learn from explicit feedback
   */
  async processFeedback(action: string, userResponse: 'approved' | 'rejected' | 'edited', edits?: any): Promise<void> {
    if (!this.userId) throw new Error('Memory system not initialized');

    const reward = userResponse === 'approved' ? 1.0 : userResponse === 'edited' ? 0.5 : -1.0;

    // Find related procedural memory
    const procedures = await this.getMatchingProcedures(action, 0.3);

    for (const procedure of procedures) {
      // Update confidence and success rate
      procedure.timesExecuted++;
      
      if (reward > 0) {
        procedure.successRate = (procedure.successRate * (procedure.timesExecuted - 1) + reward) / procedure.timesExecuted;
        procedure.confidence = Math.min(procedure.confidence + 0.05, 1.0);
      } else {
        procedure.successRate = (procedure.successRate * (procedure.timesExecuted - 1)) / procedure.timesExecuted;
        procedure.confidence = Math.max(procedure.confidence - 0.1, 0.0);
      }

      // If user edited, learn from the changes
      if (userResponse === 'edited' && edits) {
        procedure.metadata.examples.push(JSON.stringify(edits));
        // Could analyze edits to adjust pattern
      }

      // Store updated procedure
      await blink.db.searchIndex.update(procedure.id, {
        content: JSON.stringify(procedure),
        updatedAt: new Date().toISOString()
      });
    }

    // Store feedback as episodic memory
    await this.storeEpisode({
      userId: this.userId,
      timestamp: new Date(),
      type: 'interaction',
      context: {
        what: `User ${userResponse} action: ${action}`,
        who: [this.userId],
        outcome: `Feedback: ${reward}`,
        sentiment: reward > 0 ? 'positive' : reward < 0 ? 'negative' : 'neutral'
      },
      tags: ['feedback', 'learning', action],
      relatedMemories: procedures.map(p => p.id)
    });
  }

  /**
   * Analyze implicit learning from user actions
   */
  async analyzeImplicitBehavior(observations: Array<{
    hypothesis: string;
    evidence: string[];
    confidence: number;
  }>): Promise<void> {
    if (!this.userId) throw new Error('Memory system not initialized');

    const semantic = await this.getSemanticMemory();

    // Update learned preferences based on observations
    for (const obs of observations) {
      if (obs.confidence >= 0.7) {
        // High confidence - update semantic memory
        const key = obs.hypothesis.toLowerCase().replace(/\s+/g, '_');
        semantic.learnedPreferences[key] = {
          hypothesis: obs.hypothesis,
          evidence: obs.evidence,
          confidence: obs.confidence,
          learnedAt: new Date().toISOString()
        };
        semantic.confidenceScores[key] = obs.confidence;
      }
    }

    await this.updateSemanticMemory(semantic);
  }

  /**
   * Self-reflection and meta-learning
   */
  async performSelfReflection(): Promise<{
    successes: string[];
    failures: string[];
    improvements: string[];
  }> {
    if (!this.userId) throw new Error('Memory system not initialized');

    // Get recent procedural memories
    const procedures = await blink.db.searchIndex.list({
      where: {
        userId: this.userId,
        itemType: 'procedural_memory'
      },
      limit: 50
    });

    const parsed = procedures.map(p => {
      const proc = typeof p.content === 'string' ? safeJsonParse(p.content) : p.content;
      return { ...proc, id: p.id } as ProceduralMemory;
    });

    // Analyze performance
    const successes = parsed
      .filter(p => p.successRate > 0.8 && p.timesExecuted > 5)
      .map(p => p.pattern.trigger);

    const failures = parsed
      .filter(p => p.successRate < 0.3 && p.timesExecuted > 3)
      .map(p => p.pattern.trigger);

    // Generate improvement hypotheses using AI
    const { text } = await blink.ai.generateText({
      prompt: `Analyze these automated workflow patterns and suggest improvements:

Successful patterns (>80% success rate):
${successes.join('\n')}

Failed patterns (<30% success rate):
${failures.join('\n')}

Generate 3-5 specific improvements to increase success rate.`,
      maxTokens: 500
    });

    const improvements = text.split('\n').filter(line => line.trim()).slice(0, 5);

    return { successes, failures, improvements };
  }

  // Helper methods

  private calculateRelevance(query: string, text: string, title: string): number {
    const queryLower = query.toLowerCase();
    const textLower = text.toLowerCase();
    const titleLower = title.toLowerCase();

    let score = 0;

    // Exact title match
    if (titleLower === queryLower) score += 1.0;
    else if (titleLower.includes(queryLower)) score += 0.7;
    else if (queryLower.includes(titleLower)) score += 0.5;

    // Text contains query
    if (textLower.includes(queryLower)) score += 0.5;

    // Word overlap
    const queryWords = queryLower.split(/\s+/);
    const textWords = textLower.split(/\s+/);
    const overlap = queryWords.filter(w => textWords.includes(w)).length;
    score += (overlap / queryWords.length) * 0.3;

    return Math.min(score, 1.0);
  }

  private extractConditions(context: any): Array<{ field: string; operator: any; value: string }> {
    const conditions: Array<{ field: string; operator: any; value: string }> = [];

    for (const [key, value] of Object.entries(context)) {
      if (typeof value === 'string') {
        conditions.push({
          field: key,
          operator: 'equals',
          value: value
        });
      }
    }

    return conditions;
  }

  private async findSimilarProcedure(trigger: string): Promise<(ProceduralMemory & { id: string }) | null> {
    if (!this.userId) return null;

    const results = await blink.db.searchIndex.list({
      where: {
        userId: this.userId,
        itemType: 'procedural_memory'
      },
      limit: 10
    });

    for (const result of results) {
      const procedure = typeof result.content === 'string' ? safeJsonParse(result.content) : result.content;
      if (this.calculateRelevance(trigger, procedure.pattern.trigger, procedure.pattern.trigger) > 0.8) {
        return { ...procedure, id: result.id };
      }
    }

    return null;
  }

  private deepMerge(target: any, source: any): any {
    const output = { ...target };
    
    for (const key in source) {
      if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
        output[key] = this.deepMerge(target[key] || {}, source[key]);
      } else {
        output[key] = source[key];
      }
    }

    return output;
  }

  private getDefaultSemanticMemory(): SemanticMemory {
    return {
      userId: this.userId!,
      meetingPreferences: {
        preferredTimes: {
          monday: { preferred: '10:00-12:00', avoid: 'after 16:00' },
          tuesday: { preferred: '09:00-17:00', avoid: '' },
          wednesday: { preferred: '09:00-17:00', avoid: '' },
          thursday: { preferred: '09:00-17:00', avoid: '' },
          friday: { preferred: 'morning', avoid: 'after 14:00' }
        },
        maxMeetingsPerDay: 6,
        bufferTimeBetweenMeetings: 15,
        preferredDuration: 30,
        focusTimeBlocks: []
      },
      communicationStyle: {
        byContact: {},
        byContext: {
          executives: 'formal, concise',
          team: 'casual, detailed',
          clients: 'professional, responsive_within_2hrs',
          vendors: 'professional, clear'
        }
      },
      priorityRules: {
        contacts: [],
        topics: [],
        autoDecline: ['recurring meetings > 8 people'],
        autoAccept: []
      },
      workingPatterns: {
        typicalWorkHours: { start: 9, end: 17 },
        daysOff: ['saturday', 'sunday'],
        peakProductivityHours: [9, 10, 11, 14, 15],
        lowEnergyHours: [13, 16, 17]
      },
      learnedPreferences: {},
      confidenceScores: {}
    };
  }

  private getDefaultWorkingMemory(): WorkingMemory {
    return {
      userId: this.userId!,
      activeProjects: [],
      currentContext: {
        lastEmails: [],
        lastMeetings: [],
        pendingDecisions: [],
        recentInteractions: []
      },
      shortTermGoals: [],
      ttl: 3600 // 1 hour
    };
  }
}

export const selfLearningMemory = new SelfLearningMemorySystem();
